import {Link, Navigate, Route, Routes, useParams, useSearchParams} from "react-router-dom";
import {useAlert, useAuth} from "../../context";
import {useFetchJson, useFilterParam, useGetJson, useLocalFilter2, useLongPress, usePostJson, useSelect, useTouch} from "../../hooks";
import {Alert, Badge, Button, ListGroup, Modal, Stack} from "react-bootstrap";
import {
    AdminUserAlert,
    ItemLoading, MenuAdd,
    MenuBar,
    MenuClearSelectButton,
    MenuConfirmDeleteButton,
    MenuOrderBy,
    MenuSearchTerm, OurClearAlert,
    OurPagination2,
    PageTitle, SingleText,
} from "../_components";
import {capitalizeFirstLetter, endpoints} from "../../utils2";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Formik, Form as FormikForm} from "formik";
import * as yup from "yup";
import {DashboardDevice} from "./DashboardDevice";
import {ConfigureDevice} from "./configure/ConfigureDevice";
import {useSSE} from "../../hooks/useSSE";
import {AddEditAutomation} from "./eventType1/AddEditAutomation";
import {AddEditStreamPlayback} from "./eventType2/AddEditStreamPlayback";
import {AddEditStreamSender} from "./eventType2/AddEditStreamSender";
import {AddEditRelay} from "./eventType2/AddEditRelay";
import {AddEditControlledBy} from "./eventType1/AddEditControlledBy";
import {useTruncatedText} from "../../hooks";


export function DeviceList() {

    const {uid} = useParams()

    return <Routes>
        <Route index element={<CategoryForm/>}/>
        <Route path=":did/dash" element={<DashboardDevice/>}/>
        <Route path=":did/config/*" element={<ConfigureDevice/>}/>

        {/*Automation*/}
        <Route path=":did/time/:tid" element={<AddEditAutomation/>}/>
        <Route path=":did/playback/:pid" element={<AddEditStreamPlayback/>}/>
        <Route path=":did/sender/:sid" element={<AddEditStreamSender/>}/>
        <Route path=":did/relay1/:rid" element={<AddEditRelay index={1}/>}/>
        <Route path=":did/relay2/:rid" element={<AddEditRelay index={2}/>}/>
        <Route path=":did/relay3/:rid" element={<AddEditRelay index={3}/>}/>
        <Route path=":did/relay4/:rid" element={<AddEditRelay index={4}/>}/>

        {/*Controlled-by*/}
        <Route path=":did/cntr/:cid" element={<AddEditControlledBy/>}/>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={`/device/${uid}`} replace={true}/>}/>
    </Routes>
}

function CategoryForm() {
    const {uid} = useParams()
    const {authToken} = useAuth()
    const {success} = useAlert()
    const [searchParams, setSearchParams] = useSearchParams()
    const [reloadCount, setReloadCount] = useState(0)

    const pageSize = Number(searchParams.get("size")) || 10
    const pageIndex = Number(searchParams.get("index")) || 0
    const query = searchParams.get("query")
    const orderBy = searchParams.get("by")

    const tagList = ["input", "hybrid", "output"];

    const [tags, setTags] = useFilterParam("tags")

    function handleClick(tag) {
        const currentTags = tags ? tags.split("_") : []

        const updatedTags = currentTags.includes(tag)
            ? currentTags.filter(t => t !== tag)
            : [...currentTags, tag]

        setTags(updatedTags.join("_"))
    }

    function handleSelectAll() {
        setTags(null)
    }

    const filter = useMemo(() => ({
        device_classes: tags?.split("_") || [],
        // device_classes: ["input", "hybrid", "output"],
        offset: pageIndex * pageSize,
        limit: pageSize,
        query: query,
        order_by: orderBy,
        reloadCount: reloadCount,
    }), [tags, pageIndex, pageSize, query, orderBy, reloadCount])


    const [devList, loadingDeviceList, errorDeviceList, clearErrorDeviceList, setDeviceList] = useGetJson(endpoints.device.listFilter(uid), authToken?.jwt, null, filter, "POST", [filter])
    const deviceList = devList?.data

    const [devCount, loadingDeviceCount, errorDeviceCount, clearErrorDeviceCount, setDeviceCount] = useGetJson(endpoints.device.countFilter(uid), authToken?.jwt, null, filter, "POST", [filter])
    const deviceCount = devCount?.data

    const [devOrder, loadingDeviceOrder, errorDeviceOrder, clearErrorDeviceOrder, setDeviceOrder] = useGetJson(endpoints.device.orderOptions(), authToken?.jwt)
    const orderOptions = devOrder?.data || []

    const [lastSseUpdate, sseUpdatesLoading, sseUpdatesError, clearSseUUpdatesError, _5, _6, onlineList] = useSSE(endpoints.push.all_devs(uid, authToken.jwt), ["online"])

    const filteredList = useLocalFilter2(deviceList, ["name"])

    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(deviceList)

    const loading = loadingDeviceCount || loadingDeviceList || loadingDeviceOrder

    const {exec: deleteItem, error: errDeleteItem, clearError: clearErrDeleteItem} = useFetchJson(authToken?.jwt, null, "POST")

    function handleOnItemsDelete() {
        Promise.all(selList.map(item => new Promise((resolve, reject) =>
            deleteItem(endpoints.device.delete(uid, item.id), null, () => resolve(item.id))).catch(err => console.warn(err))))
            .then(ids => {
                success(`${ids.length} device(s) deactivated!`)
                setDeviceList(prev => ({...prev, data: prev.data.filter(item => !ids.includes(item.id))}))
                setDeviceCount(prev => ({...prev, data: prev.data - 1}))
                clearSelectItems()
            })
    }

    return <Stack>

        <AdminUserAlert/>
        <OurClearAlert variant="danger" msg={errorDeviceList} onClear={clearErrorDeviceList}/>
        <OurClearAlert variant="danger" msg={errorDeviceCount} onClear={clearErrorDeviceCount}/>
        <OurClearAlert variant="danger" msg={errorDeviceOrder} onClear={clearErrorDeviceOrder}/>
        <OurClearAlert variant="danger" msg={sseUpdatesError} onClear={clearSseUUpdatesError}/>
        <OurClearAlert variant="danger" msg={errDeleteItem} onClear={clearErrDeleteItem}/>

        <PageTitle
            title="Control Devices">{`Manage ${filteredList.length} of ${deviceCount} devices${tags ? " " + tags.split("_").join(", ") : ""}.`}</PageTitle>

        <MenuBar>
            {/*<div className="d-flex ms-auto gap-2">*/}
            <MenuAdd to={`/device/${uid}/activate`} icon="plus">Activate</MenuAdd>
            <MenuSearchTerm/>
            <MenuOrderBy orderOptions={orderOptions}/>
            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                {selList.length} {selList.length === 1 ? "row" : "rows"}
            </MenuClearSelectButton>
            {selList.length === 1 && <RenameItem hide={selList.length === 0} selected={selList[0]} disabled={selList.length !== 1} setList={setDeviceList}/>}
            <MenuConfirmDeleteButton hide={selList.length === 0} label="Deactivate" onSubmit={handleOnItemsDelete}>
                {selList.length} {selList.length === 1 ? "device" : "devices"}
            </MenuConfirmDeleteButton>
            {/*</div>*/}
        </MenuBar>

        <div className="d-flex justify-content-center gap-2">
            {/*<div className="mt-2">Filter by category to manage devices:</div>*/}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
                <Button className="my-2" variant={tags ? "outline-secondary" : "primary"} onClick={handleSelectAll} size="sm">All devices</Button>
                {tagList.map((tag, index) => <Button key={index} variant={tags?.split("_").includes(tag) ? "primary" : "outline-secondary"}
                                                     onClick={() => handleClick(tag)} size="sm">{capitalizeFirstLetter(tag)}</Button>)}
            </div>
        </div>

        {deviceList && deviceList.length > 0 ?
            <ListGroup variant="flush">
                <ItemLoading show={loading}/>
                {filteredList.map(item =>
                    <RenderItem key={item.id}
                                item={item}
                                selected={isItemSelected(item.id)}
                                selectedCount={selList.length}
                                onSelect={toggleSelectItems}
                                to={`/device/${uid}/${item.id}/dash`}
                                toConfig={`/device/${uid}/${item.id}/config`}
                                online={onlineList.find(obj => obj.serial === item.device_serial)?.state}
                    />
                )}
            </ListGroup>
            :
            <span className="text-center">{`No devices added yet`}</span>
        }

        <OurPagination2 loading={loading} itemCount={deviceCount}/>

    </Stack>
}

function RenderItem({item, selected, selectedCount, onSelect, to, toConfig, online}) {
    const {uid} = useParams()
    const {authToken} = useAuth()
    const touch = useTouch()
    const [show, setShow] = useState(false)
    // const update = {online:false}

    const ref = useRef(null)
    useLongPress(ref, () => onSelect(item.id))

    return (
        <Stack direction="horizontal" gap="2" className={`mb-2 rounded ${(selected || show) && "bg-light-subtle"}`}
               onMouseEnter={() => setShow(true)}
               onMouseLeave={() => setShow(false)}>
            <Stack ref={ref} direction="horizontal" className="flex-grow-1" gap="2">
                <div>
                    {/*<i className="bi bi-file fs-3"/>*/}
                    {/*<i className={`bi bi-file fs-2 ${online ? "text-success" : "text-danger"}`}/>*/}
                    <i className={`fs-3 ${online ? "bi bi-cloud text-success" : "bi bi-cloud-slash text-danger"}`}/>
                </div>
                <div>
                    <Stack direction="vertical" className="" gap={0}>
                        <Link to={to}
                              className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                            {/*<span className="text-wrap fw-semibold fs-5">{item.name}</span>*/}
                            <span className="fw-semibold fs-5">{useTruncatedText(item.name, 25)}</span>
                        </Link>
                        <span className="text-wrap">{item.device_project}</span>
                        {/*<Link to={toConfig}*/}
                        {/*      className=" link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">*/}
                        {/*    <span className="">Configure</span>*/}
                        {/*</Link>*/}
                    </Stack>
                </div>
                <div className="ms-auto">
                    <Stack className="text-body-tertiary align-items-end justify-content-center" gap={1}>
                        {!item.activation && <Badge bg="danger">Subscription Expired</Badge>}
                        {/*<Badge bg={configuration.variant}>{configuration.name}</Badge>*/}
                        {/*<span className="text-nowrap fw-normal text-secondary">{item.phone}</span>*/}
                        {/*<span className="text-nowrap fw-normal text-secondary">{`${item.region} ${item.country}`}</span>*/}
                    </Stack>
                </div>
            </Stack>

            <div className="ms-auto ">
                <div className="d-flex gap-0">
                    {selectedCount === 0 && <Button as={Link} to={toConfig} variant="link">
                        <i className="bi bi-gear fs-5"/>
                    </Button>}
                    {(!touch || selected || selectedCount > 0) &&
                        <Button className="link-underline link-underline-opacity-0" variant="link"
                                onClick={() => onSelect(item.id)}>
                            <span className={`bi ${selected ? "bi-check-square" : "bi-square"} fs-5`}></span>
                        </Button>
                    }
                </div>
            </div>
        </Stack>
    )
}

function RenameItem({hide, selected, disabled, setList}) {
    const [show, setShow] = useState(false);
    const {uid} = useParams()
    const {authToken} = useAuth()

    const [sendData, _2, postLoading, postError, clearPostError] = usePostJson(endpoints.device.edit(uid, selected.id), authToken?.jwt)

    const initialValues = {
        name: selected.name,
    }

    const validationSchema = yup.object().shape({
        name: yup.string().required("Name is required."),
    })

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    function handleOnSubmit(values, actions) {
        sendData(values, newValues => {
            // actions.setSubmitting(false)
            // setList(selected, values)
            setList(l => ({...l, data: l.data.map(i => i.id === selected.id ? {...i, name: values.name} : i)}))
            handleClose()
        })
    }

    if (hide)
        return null

    return (
        <>
            <Button variant="primary" onClick={handleShow} disabled={disabled}>
                <span className="d-none d-sm-inline">Rename</span>
                <i className="bi bi-pencil d-sm-none"/>
            </Button>

            <Modal show={show} onHide={handleClose}>

                <Modal.Header closeButton>
                    <Modal.Title>Rename Device</Modal.Title>
                </Modal.Header>

                <Alert variant="danger" show={postError} dismissible onClose={clearPostError}>{postError}</Alert>

                <Formik validationSchema={validationSchema} onSubmit={handleOnSubmit}
                        initialValues={initialValues} enableReinitialize={true}>
                    {({isSubmitting, submitForm}) => (
                        <FormikForm
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submitForm();
                                }
                            }}
                        >

                            <Modal.Body>
                                <SingleText name="name" label="Name" maxLength="64"/>
                            </Modal.Body>

                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleClose}>
                                    Close
                                </Button>
                                <Button type="submit" variant="primary" disabled={isSubmitting}>Edit</Button>
                            </Modal.Footer>

                        </FormikForm>
                    )}
                </Formik>
            </Modal>
        </>
    );
}